import { useState, useMemo, useCallback, useEffect } from 'react';
import { View, Views, NavigateAction } from 'react-big-calendar';
import { useMediaQuery, useTheme } from '@mui/material';
import { RBCEvent, Appointment, UpdateAppointmentPayload } from '../types';
import { DEFAULT_AGENDA_LENGTH } from '../constants';
import { useNotification } from '../../../contexts/NotificationContext';
import { formatISOWithTimezone, formatDateToDateTimeLocal, parseSlotAsWallClock } from '../../BookingConfiguration/utils';
import { isWorkingDayForConfig } from '../utils/slotAvailability';
import {
  GeneratedSlot,
  buildGeneratedSlotLookup,
  getGeneratedSlotKey,
} from '../utils/generatedAvailability';

export const useCalendarLogic = (
  appointments: Appointment[],
  config: any,
  updateAppointment: (id: string, payload: UpdateAppointmentPayload) => Promise<boolean>,
  availableSlots: GeneratedSlot[],
  getNow?: () => Date
) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showNotification } = useNotification();

  const getOriginalEventEnd = useCallback((event: RBCEvent): Date | null => {
    const resourceEnd = event.resource?.slot_end;
    if (resourceEnd) {
      return parseSlotAsWallClock(resourceEnd) ?? new Date(resourceEnd);
    }

    return event.end ?? null;
  }, []);

  const isPastAppointmentEvent = useCallback((event: RBCEvent, now: Date): boolean => {
    const originalEnd = getOriginalEventEnd(event);
    return !!originalEnd && originalEnd < now;
  }, [getOriginalEventEnd]);

  // Calendar States
  const [view, setView] = useState<View>(isMobile ? Views.DAY : Views.WEEK);
  const [date, setDate] = useState<Date>(() => getNow ? getNow() : new Date());
  const [hasInitializedTz, setHasInitializedTz] = useState(false);

  // Sync calendar date to target timezone once config loads
  useEffect(() => {
    if (config?.timezone && !hasInitializedTz && getNow) {
      setDate(getNow());
      setHasInitializedTz(true);
    }
  }, [config?.timezone, hasInitializedTz, getNow]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<RBCEvent | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [agendaLength, setAgendaLength] = useState(DEFAULT_AGENDA_LENGTH);
  const [agendaPage, setAgendaPage] = useState<number>(1);
  const [agendaLimit] = useState<number>(10);
  const [dndConfirmState, setDndConfirmState] = useState<{
    open: boolean;
    eventId: string;
    payload: UpdateAppointmentPayload;
  } | null>(null);

  // Sync view on mobile/desktop transition
  useEffect(() => {
    setView(isMobile ? Views.DAY : Views.WEEK);
  }, [isMobile]);

  /**
   * Helper to determine if a given date is a working day based on SMB config.
   * Holiday / allowed-days rendering stays config-driven (the red non-working columns).
   */
  const isWorkingDay = useCallback((date: Date) => {
    return isWorkingDayForConfig(config, date);
  }, [config]);

  /**
   * Calculate the end date for agenda range based on agendaLength.
   * agendaLength=N means show N days after start (inclusive).
   */
  const getAgendaRange = useCallback((startDate: Date, length: number): { start: Date; end: Date } => {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    // If length is 0, end is same day; otherwise add length days
    end.setDate(start.getDate() + (length === 0 ? 0 : length));
    end.setHours(23, 59, 59, 999);

    return { start, end };
  }, []);

  // ── API-driven availability ────────────────────────────────────────────
  // `availableSlots` is supplied by the backend (useAvailableSlots) — the
  // frontend no longer computes availability from config. The API already
  // excludes booked / past / non-working times and may include overnight slots.
  const availableSlotLookup = useMemo(() => {
    return buildGeneratedSlotLookup(availableSlots);
  }, [availableSlots]);

  // 15-minute cell coverage so slotPropGetter can color sub-hour slots precisely.
  // Keys use absolute ms, so overnight slots color cells in both day columns.
  const CELL_SIZE = 15;
  const availableCellLookup = useMemo(() => {
    const cellLookup = new Set<string>();
    availableSlots.forEach(slot => {
      let cellStart = slot.start.getTime();
      const slotEndMs = slot.end.getTime();
      while (cellStart < slotEndMs) {
        cellLookup.add(`${cellStart}-${cellStart + CELL_SIZE * 60000}`);
        cellStart += CELL_SIZE * 60000;
      }
    });
    return cellLookup;
  }, [availableSlots]);

  const isSlotBookable = useCallback((start: Date, end: Date) => {
    return availableSlotLookup.has(getGeneratedSlotKey(start, end));
  }, [availableSlotLookup]);

  const getDateKey = useCallback((date: Date) => formatDateToDateTimeLocal(date).split('T')[0], []);

  /**
   * Resolve a clicked grid cell to the API slot that contains it.
   * Slots can be any duration, so we match the slot whose [start, end) range
   * contains the clicked touchpoint. When two slots overlap at the touchpoint
   * (for example a previous-day overnight slot and the selected day's first
   * slot), prefer the slot that starts on the clicked calendar day.
   */
  const resolveTimedTouchpointSlot = useCallback((touchpointStart: Date): GeneratedSlot | null => {
    const matchingSlots = availableSlots.filter(
      (slot) => touchpointStart >= slot.start && touchpointStart < slot.end,
    );

    if (matchingSlots.length <= 1) return matchingSlots[0] ?? null;

    const clickedDate = getDateKey(touchpointStart);
    return matchingSlots.find((slot) => getDateKey(slot.start) === clickedDate) ?? matchingSlots[0];
  }, [availableSlots, getDateKey]);

  const handleNavigate = useCallback((newDate: Date, _view: View, action: NavigateAction) => {
    // For agenda view, handle navigation with interval-based shifting
    if (view === Views.AGENDA && (action === 'PREV' || action === 'NEXT')) {
      // Calculate interval: if agendaLength is 0, shift by 1 day; otherwise shift by (agendaLength) days
      const interval = agendaLength === 0 ? 1 : agendaLength;
      const stepped = new Date(date);
      stepped.setDate(stepped.getDate() + (action === 'NEXT' ? interval : -interval));
      setDate(stepped);
      // Reset to first page when navigating
      setAgendaPage(1);
      return;
    }

    setDate(newDate);

    // When clicking "Today" in agenda view, show only today
    if (action === 'TODAY' && view === Views.AGENDA) {
      setAgendaLength(0);
      setAgendaPage(1);
    }
  }, [view, agendaLength, date]);

  const handleDateRangeApply = useCallback((start: Date, end: Date) => {
    // RBC Agenda uses endOf(date + length, 'day') for filtering, making the end date inclusive.
    // So length=N actually shows N+1 days. We compute the day count and subtract 1.
    const daysDiff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    setDate(start);
    setAgendaLength(daysDiff);
    // Reset to first page when applying new range
    setAgendaPage(1);
  }, []);

  /**
   * Handle page change in agenda view pagination.
   */
  const handleAgendaPageChange = useCallback((newPage: number) => {
    setAgendaPage(newPage);
  }, []);

  // Memoized Filtered Events
  // Uses parseSlotAsWallClock so events plot at the business's local wall-clock time
  // regardless of the browser's timezone.
  const events = useMemo(() => {
    return appointments
      .filter(apt => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
          apt.lead_name?.toLowerCase().includes(term) ||
          apt.lead_phone?.includes(term) ||
          apt.services?.some(s => s.toLowerCase().includes(term))
        );
      })
      .map(apt => {
        const start = parseSlotAsWallClock(apt.slot_start) ?? new Date(apt.slot_start);
        const end = parseSlotAsWallClock(apt.slot_end) ?? new Date(apt.slot_end);
        return {
          id: apt.id,
          start,
          end,
          allDay: false,
          resource: apt,
          status: apt.status
        };
      });
  }, [appointments, searchTerm]);

  /**
   * handleSelectSlot — fires on both:
   *  • clicking a timed grid cell (week/day view)
   *  • clicking the "All Day" row (week/day view: allDay=true)
   *  • clicking any cell in month view
   *
   * KEY RULE: Detect all-day FIRST, then validate. Never use midnight time as
   * an all-day heuristic — 12:00 AM is a valid timed slot.
   */
  const handleSelectSlot = useCallback((slotInfo: { start: Date; end: Date; action: string; allDay?: boolean }) => {
    // Disable drag selection entirely
    if (slotInfo.action === 'select') return;

    // All-day: explicit RBC flag (All Day row in week/day) OR month view (every click is date-level)
    const isAllDayClick = slotInfo.allDay || view === Views.MONTH;

    if (isAllDayClick) {
      if (!isWorkingDay(slotInfo.start)) {
        showNotification('Selected day is a non-working day. Please choose another date.', 'warning');
        return;
      }

      const now = getNow ? getNow() : new Date();
      const endOfDay = new Date(slotInfo.start);
      endOfDay.setHours(23, 59, 59, 999);

      if (endOfDay < now) {
        showNotification('Cannot book an appointment in the past. Please select a future date.', 'warning');
        return;
      }

      // Open the modal at the clicked day. The modal loads that day's API slots
      // and the user picks a specific bookable slot.
      const dayStart = new Date(slotInfo.start);
      dayStart.setHours(0, 0, 0, 0);
      setSelectedEvent({ id: '', title: '', start: dayStart, end: dayStart });
      setModalOpen(true);
      return;
    }

    // ── Timed cell click ────────────────────────────────────────────────
    const now = getNow ? getNow() : new Date();
    if (slotInfo.start < now) {
      showNotification('Cannot book a slot in the past. Please select a future time.', 'warning');
      return;
    }

    const clickedStart = new Date(slotInfo.start);
    const resolvedSlot = resolveTimedTouchpointSlot(clickedStart);

    if (!resolvedSlot) {
      showNotification('This slot is not available for booking.', 'warning');
      return;
    }

    setSelectedEvent({
      id: '',
      title: '',
      start: resolvedSlot.start,
      end: resolvedSlot.end,
      modalDate: getDateKey(clickedStart),
    });
    setModalOpen(true);
  }, [view, isWorkingDay, getNow, resolveTimedTouchpointSlot, showNotification, getDateKey]);

  const getEventModalDate = useCallback((event: RBCEvent) => {
    if (!event.start || !event.end) return event.modalDate;

    const startDate = getDateKey(event.start);
    const endDate = getDateKey(event.end);
    return startDate !== endDate ? endDate : event.modalDate;
  }, [getDateKey]);

  const handleSelectEvent = useCallback((event: RBCEvent) => {
    setSelectedEvent({
      ...event,
      modalDate: getEventModalDate(event),
    });
    setModalOpen(true);
  }, [getEventModalDate]);

  /**
   * handleEventDrop — fires when user drags an existing appointment to a new slot.
   *
   * Cases:
   *  1. allDay=true (dropped onto All Day row in week/day view) → open modal at that day
   *  2. month view drop → treat as all-day, open modal
   *  3. timed cell drop → resolve to the containing API slot & show confirm dialog
   *
   * KEY RULE: Do NOT use midnight-time check for all-day detection.
   * 12:00 AM is a valid timed slot and must NOT trigger all-day behaviour.
   */
  const handleEventDrop = useCallback(async ({ event, start, allDay, isAllDay }: any) => {
    if (!event.resource?.id) return;
    const rawStart = new Date(start);
    const timezone = config?.timezone || 'UTC';
    const now = getNow ? getNow() : new Date();

    if (isPastAppointmentEvent(event, now)) {
      showNotification('Past appointments cannot be rescheduled.', 'warning');
      return;
    }

    // ── All-day row drop OR month view drop ─────────────────────────────
    // RBC DnD may name the flag `allDay` or `isAllDay` depending on version — handle both.
    const isAllDayDrop = !!(allDay || isAllDay) || view === Views.MONTH;

    if (isAllDayDrop) {
      if (!isWorkingDay(rawStart)) {
        showNotification('Cannot move appointment to a non-working day.', 'warning');
        return;
      }

      const endOfDay = new Date(rawStart);
      endOfDay.setHours(23, 59, 59, 999);

      if (endOfDay < now) {
        showNotification('Cannot move appointment to a past date.', 'warning');
        return;
      }

      // Open modal at the target day — user picks the specific available slot.
      const dayStart = new Date(rawStart);
      dayStart.setHours(0, 0, 0, 0);
      setSelectedEvent({ ...event, start: dayStart, end: dayStart });
      setModalOpen(true);
      return;
    }

    const resolvedSlot = resolveTimedTouchpointSlot(rawStart);
    if (!resolvedSlot) {
      showNotification('This slot is not available for booking.', 'warning');
      return;
    }

    const newStart = resolvedSlot.start;
    const newEnd = resolvedSlot.end;

    // ── Skip if nothing actually changed (timed drops only) ──────────────
    if (event.start.getTime() === newStart.getTime() && event.end.getTime() === newEnd.getTime()) {
      return;
    }

    // ── Timed-cell drop ──────────────────────────────────────────────────
    if (newStart < now) {
      showNotification('Cannot move appointment to a past time slot.', 'warning');
      return;
    }

    setDndConfirmState({
      open: true,
      eventId: event.resource.id,
      payload: {
        slot_start: formatISOWithTimezone(formatDateToDateTimeLocal(newStart), timezone),
        slot_end: formatISOWithTimezone(formatDateToDateTimeLocal(newEnd), timezone)
      }
    });
  }, [view, config, getNow, isWorkingDay, isPastAppointmentEvent, resolveTimedTouchpointSlot, showNotification]);

  const handleConfirmDnd = async () => {
    if (dndConfirmState) {
      await updateAppointment(dndConfirmState.eventId, dndConfirmState.payload);
      setDndConfirmState(null);
    }
  };

  const handleCancelDnd = () => setDndConfirmState(null);

  return {
    view, setView,
    date, setDate,
    modalOpen, setModalOpen,
    selectedEvent, setSelectedEvent,
    searchTerm, setSearchTerm,
    agendaLength,
    agendaPage,
    agendaLimit,
    events,
    isWorkingDay,
    availableCellLookup,
    isSlotBookable,
    resolveTimedTouchpointSlot,
    handleNavigate,
    handleDateRangeApply,
    handleAgendaPageChange,
    handleSelectSlot,
    handleSelectEvent,
    handleEventDrop,
    dndConfirmState,
    handleConfirmDnd,
    handleCancelDnd,
    getAgendaRange
  };
};
